import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Database,
  Gauge,
  Loader2,
  Save,
  Search,
  Settings2,
  SlidersHorizontal,
  User,
  Users,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { clientApi, transformClientData } from '../../../api/clientApi';
import { scraperQuotaApi } from '../../../api/scraperQuotaApi';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

type ClientRow = {
  id: number;
  name: string;
  email: string;
  isActive: boolean;
};

type ConfigForm = {
  auto_run_per_day: number;
  extra_runs_limit: number;
  extra_run_period: string;
  max_articles_per_url: number;
  max_urls_per_moderator_run: number;
  scraper_enabled: boolean;
  reason: string;
};

type NumericField =
  | 'auto_run_per_day'
  | 'extra_runs_limit'
  | 'max_articles_per_url'
  | 'max_urls_per_moderator_run';

const EXTRA_PERIODS = ['daily', 'weekly', 'monthly'] as const;
const PAGE_SIZE = 8;
const GENERIC_NAME_TOKENS = new Set(['moderator', 'mod', 'mr', 'mrs', 'ms', 'dr']);

const defaultForm: ConfigForm = {
  auto_run_per_day: 1,
  extra_runs_limit: 3,
  extra_run_period: 'weekly',
  max_articles_per_url: 200,
  max_urls_per_moderator_run: 50,
  scraper_enabled: true,
  reason: '',
};

const numericFieldDefaults: Record<NumericField, number> = {
  auto_run_per_day: defaultForm.auto_run_per_day,
  extra_runs_limit: defaultForm.extra_runs_limit,
  max_articles_per_url: defaultForm.max_articles_per_url,
  max_urls_per_moderator_run: defaultForm.max_urls_per_moderator_run,
};

const numericFieldMins: Record<NumericField, number> = {
  auto_run_per_day: 0,
  extra_runs_limit: 0,
  max_articles_per_url: 1,
  max_urls_per_moderator_run: 1,
};

const numericFieldMaxes: Partial<Record<NumericField, number>> = {
  max_articles_per_url: 200,
};

const glassPanel =
  'rounded-2xl border border-white/50 bg-white/40 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150 ring-1 ring-indigo-200/35';

const glassCard =
  'rounded-2xl border border-white/50 bg-white/45 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150 ring-1 ring-indigo-200/30';

const inputClass =
  'h-10 rounded-xl border border-white/60 bg-white/90 text-[#0f172a] shadow-sm placeholder:text-slate-400 focus-visible:border-[#1d4ed8]/40 focus-visible:ring-[3px] focus-visible:ring-[#1d4ed8]/15';

function buildNumericDrafts(cfg: ConfigForm): Record<NumericField, string> {
  return {
    auto_run_per_day: String(cfg.auto_run_per_day),
    extra_runs_limit: String(cfg.extra_runs_limit),
    max_articles_per_url: String(cfg.max_articles_per_url),
    max_urls_per_moderator_run: String(cfg.max_urls_per_moderator_run),
  };
}

function normalizeNumericValue(
  raw: string,
  min: number,
  fallback: number,
  max?: number,
): number {
  const trimmed = raw.trim();
  const parsed = trimmed === '' ? fallback : Number(trimmed);
  if (!Number.isFinite(parsed)) return fallback;
  const bounded = max == null ? parsed : Math.min(parsed, max);
  return Math.max(min, bounded);
}

function errMsg(e: unknown, fallback = 'Request failed'): string {
  const ax = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && 'message' in detail) {
    return String((detail as { message?: string }).message || fallback);
  }
  if (Array.isArray(detail)) {
    return detail.map((x: { msg?: string }) => x?.msg).filter(Boolean).join(', ') || fallback;
  }
  return ax.message || fallback;
}

function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, ''))
    .filter(Boolean);
  if (!parts.length) return '?';

  const meaningfulParts = parts.filter((part) => !GENERIC_NAME_TOKENS.has(part.toLowerCase()));
  const source = meaningfulParts.length ? meaningfulParts : parts;

  if (source.length >= 2) {
    const last = source[source.length - 1];
    const prev = source[source.length - 2];
    return `${prev[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
  }

  const token = source[0];
  if (token.length === 1) return token.toUpperCase();
  return `${token[0] ?? ''}${token[token.length - 1] ?? ''}`.toUpperCase();
}

function ToggleSwitch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className="flex items-center gap-3 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
          checked
            ? 'bg-gradient-to-r from-[#0284c7] to-[#1d4ed8] shadow-[0_2px_8px_rgba(29,78,216,0.28)]'
            : 'bg-slate-200'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
            checked ? 'translate-x-[1.35rem]' : 'translate-x-0.5'
          }`}
        />
      </span>
      <span className="text-sm font-semibold text-[#0f172a]">{label}</span>
    </button>
  );
}

function FieldHelp({ children }: { children: ReactNode }) {
  return <p className="text-[11px] leading-snug text-slate-500">{children}</p>;
}

function SectionIcon({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0284c7] to-[#1d4ed8] text-white shadow-[0_4px_12px_rgba(29,78,216,0.25)]">
      {children}
    </span>
  );
}

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <Badge
      variant="outline"
      className={`shrink-0 rounded-full border px-2.5 py-0 text-[11px] font-semibold leading-5 ${
        active
          ? 'border-emerald-200/80 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-slate-50 text-slate-500'
      }`}
    >
      {label}
    </Badge>
  );
}

function toClientRow(raw: {
  id: string | number;
  name: string;
  email: string;
  isActive: boolean;
}): ClientRow | null {
  const id = Number(raw.id);
  if (!Number.isFinite(id)) return null;
  return {
    id,
    name: raw.name,
    email: raw.email,
    isActive: Boolean(raw.isActive),
  };
}

function parseClientIdParam(raw: string | null): number | null {
  if (raw == null || raw.trim() === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function SettingsPage() {
  const { t } = useUiLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkClientId = useMemo(
    () => parseClientIdParam(searchParams.get('clientId')),
    [searchParams],
  );
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [clientId, setClientId] = useState<number | null>(deepLinkClientId);
  const [selectedClient, setSelectedClient] = useState<ClientRow | null>(null);
  const [form, setForm] = useState<ConfigForm>(defaultForm);
  const [savedForm, setSavedForm] = useState<ConfigForm>(defaultForm);
  const [numericDrafts, setNumericDrafts] = useState<Record<NumericField, string>>(
    buildNumericDrafts(defaultForm),
  );
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [articlesError, setArticlesError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(totalClients / PAGE_SIZE));
  const effectivePage = Math.min(page, totalPages);
  const rangeStart = totalClients === 0 ? 0 : (effectivePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = clients.length === 0 ? 0 : rangeStart + clients.length - 1;

  const selected = useMemo(
    () => clients.find((c) => c.id === clientId) || selectedClient || null,
    [clients, clientId, selectedClient],
  );

  const selectClient = useCallback(
    (row: ClientRow, { syncUrl = true }: { syncUrl?: boolean } = {}) => {
      setClientId(row.id);
      setSelectedClient(row);
      if (syncUrl) {
        setSearchParams({ clientId: String(row.id) }, { replace: true });
      }
    },
    [setSearchParams],
  );

  const loadClients = useCallback(async (targetPage: number, searchValue: string) => {
    setLoading(true);
    try {
      const skip = Math.max(0, (targetPage - 1) * PAGE_SIZE);
      const raw = await clientApi.getClientsPaginated({
        skip,
        limit: PAGE_SIZE,
        search: searchValue,
      });
      const rawItems = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
      const list = transformClientData(rawItems);
      const seen = new Set<number>();
      const rows: ClientRow[] = [];
      for (const r of list as Array<{
        id: string | number;
        name: string;
        email: string;
        isActive: boolean;
      }>) {
        const row = toClientRow(r);
        if (!row || seen.has(row.id)) continue;
        seen.add(row.id);
        rows.push(row);
      }
      setClients(rows);
      const nextTotal = Number(raw?.total);
      setTotalClients(Number.isFinite(nextTotal) ? nextTotal : rows.length);
    } catch (e) {
      toast.error(errMsg(e, t('settings.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const applyConfig = useCallback((cfg: Record<string, unknown>) => {
    const next: ConfigForm = {
      auto_run_per_day: Number(cfg.auto_run_per_day ?? 1),
      extra_runs_limit: Number(cfg.extra_runs_limit ?? 3),
      extra_run_period: String(cfg.extra_run_period || 'weekly'),
      max_articles_per_url: Number(cfg.max_articles_per_url ?? 200),
      max_urls_per_moderator_run: Number(cfg.max_urls_per_moderator_run ?? 50),
      scraper_enabled: Boolean(cfg.scraper_enabled),
      reason: '',
    };
    setForm(next);
    setSavedForm(next);
    setNumericDrafts(buildNumericDrafts(next));
    setArticlesError(null);
  }, []);

  const loadConfig = useCallback(
    async (id: number) => {
      setConfigLoading(true);
      try {
        const cfgRes = await scraperQuotaApi.getConfig(id);
        applyConfig(cfgRes.data as Record<string, unknown>);
      } catch (e) {
        setForm(defaultForm);
        setSavedForm(defaultForm);
        toast.error(errMsg(e, t('settings.loadFailed')));
      } finally {
        setConfigLoading(false);
      }
    },
    [applyConfig, t],
  );

  // Deep-link from dashboard: /settings?clientId=… (select by id, no search filter)
  useEffect(() => {
    if (deepLinkClientId == null) return;
    setClientId(deepLinkClientId);
    setSearch('');
    let cancelled = false;
    (async () => {
      try {
        const data = await clientApi.getClientById(deepLinkClientId);
        const list = transformClientData(Array.isArray(data) ? data : [data]);
        const row = list.length
          ? toClientRow(
              list[0] as {
                id: string | number;
                name: string;
                email: string;
                isActive: boolean;
              },
            )
          : null;
        if (cancelled || !row) return;
        setSelectedClient(row);

        // Jump to the page that contains this moderator (unfiltered list).
        const allRaw = await clientApi.getClients();
        const allList = transformClientData(Array.isArray(allRaw) ? allRaw : []) as Array<{
          id: string | number;
        }>;
        const idx = allList.findIndex((c) => Number(c.id) === deepLinkClientId);
        if (!cancelled && idx >= 0) {
          setPage(Math.floor(idx / PAGE_SIZE) + 1);
        }
      } catch {
        // Config can still load by id; list selection falls back when paginated data arrives.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepLinkClientId]);

  useEffect(() => {
    loadClients(page, search);
  }, [loadClients, page, search]);

  useEffect(() => {
    if (clientId != null) loadConfig(clientId);
  }, [clientId, loadConfig]);

  useEffect(() => {
    if (!clients.length) {
      // Keep deep-linked / explicitly selected moderator even if search returns empty.
      if (clientId != null) return;
      if (selectedClient !== null) {
        setSelectedClient(null);
      }
      return;
    }
    const inPage = clients.find((row) => row.id === clientId);
    if (inPage) {
      if (!selectedClient || selectedClient.id !== inPage.id) {
        setSelectedClient(inPage);
      }
      return;
    }
    // Don't steal focus away from an explicit selection (deep link or prior pick).
    if (clientId != null) return;
    const first = clients[0];
    if (first) {
      selectClient(first, { syncUrl: false });
    }
  }, [clients, clientId, selectedClient, selectClient]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const validateArticles = useCallback(
    (value: number): boolean => {
      if (!Number.isFinite(value) || value < 1 || value > 200) {
        setArticlesError(t('settings.errMaxArticles'));
        return false;
      }
      setArticlesError(null);
      return true;
    },
    [t],
  );

  const onCancel = () => {
    setForm({ ...savedForm, reason: '' });
    setNumericDrafts(buildNumericDrafts(savedForm));
    setArticlesError(null);
  };

  const normalizeField = useCallback(
    (field: NumericField, raw: string): number =>
      normalizeNumericValue(
        raw,
        numericFieldMins[field],
        numericFieldDefaults[field],
        numericFieldMaxes[field],
      ),
    [],
  );

  const commitNumericField = useCallback(
    (field: NumericField) => {
      setNumericDrafts((drafts) => {
        const normalized = normalizeField(field, drafts[field]);
        setForm((prev) => ({ ...prev, [field]: normalized }));
        if (field === 'max_articles_per_url') {
          validateArticles(normalized);
        }
        return { ...drafts, [field]: String(normalized) };
      });
    },
    [normalizeField, validateArticles],
  );

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    if (clientId == null) return;
    const normalizedForm: ConfigForm = {
      ...form,
      auto_run_per_day: normalizeField('auto_run_per_day', numericDrafts.auto_run_per_day),
      extra_runs_limit: normalizeField('extra_runs_limit', numericDrafts.extra_runs_limit),
      max_urls_per_moderator_run: normalizeField(
        'max_urls_per_moderator_run',
        numericDrafts.max_urls_per_moderator_run,
      ),
      max_articles_per_url: normalizeField('max_articles_per_url', numericDrafts.max_articles_per_url),
    };
    setForm(normalizedForm);
    setNumericDrafts(buildNumericDrafts(normalizedForm));
    if (!validateArticles(normalizedForm.max_articles_per_url)) {
      toast.error(t('settings.errMaxArticles'));
      return;
    }
    setSaving(true);
    try {
      await scraperQuotaApi.putConfig(clientId, {
        auto_run_per_day: normalizedForm.auto_run_per_day,
        extra_runs_limit: normalizedForm.extra_runs_limit,
        extra_run_period: normalizedForm.extra_run_period,
        max_articles_per_url: normalizedForm.max_articles_per_url,
        max_urls_per_moderator_run: normalizedForm.max_urls_per_moderator_run,
        scraper_enabled: normalizedForm.scraper_enabled,
        reason: normalizedForm.reason.trim() || undefined,
      });
      toast.success(t('settings.toastConfigSaved'));
      await loadConfig(clientId);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hawk-admin-surface flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0284c7]" />
      </div>
    );
  }

  return (
    <div className="hawk-admin-surface hawk-settings-surface flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="shrink-0 px-3 py-3 sm:px-4 sm:py-3.5 md:px-5">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/70 bg-white/60 text-[#1d4ed8] shadow-[0_4px_14px_rgba(15,23,42,0.06)] backdrop-blur-md">
            <Settings2 className="h-5 w-5" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-[#0f172a] sm:text-2xl">
              {t('settings.title')}
            </h1>
            <p className="mt-0.5 text-sm leading-tight text-slate-500">
              {t('settings.moderatorConfigTitle')}
            </p>
          </div>
        </div>
      </header>

      <div className="hawk-scrollbar-rainbow min-h-0 flex-1 overflow-y-auto px-3 pb-4 sm:px-4 sm:pb-5 md:px-5 xl:px-6">
        <div className="mx-auto w-full max-w-[104rem]">
          {!clients.length ? (
            <div className={`${glassPanel} px-6 py-12 text-center text-sm text-slate-500`}>
              {t('settings.noClients')}
            </div>
          ) : (
            <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] xl:gap-5">
              {/* Left — Moderators list */}
              <aside
                className={`${glassPanel} flex min-h-[26rem] flex-col overflow-hidden xl:sticky xl:top-3 xl:max-h-[calc(100vh-8rem)]`}
              >
                <div className="space-y-3 border-b border-slate-200/50 px-3.5 pb-3.5 pt-4 sm:px-4">
                  <h2 className="flex items-center gap-2 text-[15px] font-semibold text-[#0f172a]">
                    <Users className="h-4 w-4 text-[#1d4ed8]" />
                    {t('settings.moderatorsTitle')}
                  </h2>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t('settings.searchPlaceholder')}
                      className={`${inputClass} pl-9`}
                      aria-label={t('settings.searchPlaceholder')}
                    />
                  </div>
                </div>

                <ul className="hawk-settings-moderator-list-scroll min-h-0 flex-1 overflow-y-auto">
                  {clients.length === 0 ? (
                    <li className="px-4 py-10 text-center text-sm text-slate-400">
                      {t('settings.noSearchResults')}
                    </li>
                  ) : (
                    clients.map((c, index) => {
                      const active = c.id === clientId;
                      return (
                        <li
                          key={c.id}
                          className={index > 0 ? 'border-t border-slate-200/45' : undefined}
                        >
                            <button
                              type="button"
                              onClick={() => selectClient(c)}
                              className={`relative flex w-full items-start gap-3 px-3.5 py-3.5 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4ed8]/30 focus-visible:ring-inset sm:px-4 ${
                                active
                                  ? 'bg-gradient-to-r from-sky-100/90 via-blue-50/80 to-transparent shadow-[inset_3px_0_0_0_#1d4ed8]'
                                  : 'bg-transparent hover:bg-white/50'
                              }`}
                            >
                            <span
                              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold shadow-sm ${
                                active
                                  ? 'bg-gradient-to-br from-[#0284c7] to-[#1d4ed8] text-white'
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {initials(c.name)}
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5 pr-1">
                              <span className="block truncate text-sm font-semibold text-[#0f172a]">
                                {c.name}
                              </span>
                              <span className="block truncate text-[13px] text-slate-500">
                                {c.email}
                              </span>
                              <span className="block truncate text-[11px] text-slate-400">
                                {t('settings.clientMeta', { id: c.id })}
                              </span>
                            </span>
                            <span className="mt-0.5 flex shrink-0 flex-col items-end gap-1.5">
                              <StatusBadge
                                active={c.isActive}
                                label={
                                  c.isActive
                                    ? t('settings.statusActive')
                                    : t('settings.statusInactive')
                                }
                              />
                              {active && (
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1d4ed8] text-white shadow-sm">
                                  <Check className="h-3 w-3" strokeWidth={3} />
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>

                {totalClients > PAGE_SIZE && (
                  <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200/50 px-3 py-2.5 sm:px-3.5">
                    <p className="text-xs text-slate-500">
                      {t('settings.paginationRange', {
                        start: rangeStart,
                        end: rangeEnd,
                        total: totalClients,
                      })}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-xl border-slate-200/80 bg-white/80"
                        disabled={effectivePage <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        aria-label={t('settings.prevPage')}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-slate-600">
                        {effectivePage} / {totalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-xl border-slate-200/80 bg-white/80"
                        disabled={effectivePage >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        aria-label={t('settings.nextPage')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </aside>

              {/* Right — Configuration */}
              <section className="relative min-w-0 space-y-3.5 xl:sticky xl:top-3 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto hawk-settings-moderator-scroll">
                {configLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[1.25rem] bg-white/50 backdrop-blur-[2px]">
                    <Loader2 className="h-5 w-5 animate-spin text-[#0284c7]" />
                  </div>
                )}

                {!selected ? (
                  <div className={`${glassPanel} px-6 py-12 text-center text-sm text-slate-500`}>
                    {t('settings.selectModerator')}
                  </div>
                ) : (
                  <form onSubmit={onSave} className="space-y-3.5">
                    {/* Profile summary */}
                    <div className={`${glassCard} flex items-center gap-4 px-4 py-4 sm:px-5`}>
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0284c7] to-[#1d4ed8] text-base font-semibold text-white shadow-[0_6px_16px_rgba(29,78,216,0.3)]">
                        {initials(selected.name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-[15px] font-bold text-[#0f172a]">
                            {selected.name}
                          </p>
                          <StatusBadge
                            active={selected.isActive}
                            label={
                              selected.isActive
                                ? t('settings.statusActive')
                                : t('settings.statusInactive')
                            }
                          />
                        </div>
                        <p className="mt-0.5 truncate text-sm text-slate-500">{selected.email}</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          {t('settings.clientMeta', { id: selected.id })}
                        </p>
                      </div>
                      <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200/70 bg-white/80 text-[#1d4ed8] shadow-sm sm:flex">
                        <User className="h-4 w-4" />
                      </span>
                    </div>

                    {/* Auto quota */}
                    <div className={`${glassCard} px-4 py-4 sm:px-5 sm:py-5`}>
                      <div className="mb-4 flex items-start gap-3">
                        <SectionIcon>
                          <Gauge className="h-4 w-4" strokeWidth={2.25} />
                        </SectionIcon>
                        <div className="min-w-0 pt-0.5">
                          <h3 className="text-[15px] font-bold text-[#0f172a]">
                            {t('settings.sectionAutoQuota')}
                          </h3>
                          <p className="mt-0.5 text-xs leading-snug text-slate-500">
                            {t('settings.sectionAutoQuotaDesc')}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-4">
                        <div className="space-y-1.5">
                          <Label className="text-[13px] font-medium text-slate-700">
                            {t('settings.autoRunsPerDay')}
                          </Label>
                          <Input
                            type="number"
                            min={0}
                            className={inputClass}
                            value={numericDrafts.auto_run_per_day}
                            onChange={(e) =>
                              setNumericDrafts((drafts) => ({
                                ...drafts,
                                auto_run_per_day: e.target.value,
                              }))
                            }
                            onBlur={() => commitNumericField('auto_run_per_day')}
                          />
                          <FieldHelp>{t('settings.helpAutoRuns')}</FieldHelp>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-[13px] font-medium text-slate-700">
                            {t('settings.extraRunsPerPeriod')}
                          </Label>
                          <Input
                            type="number"
                            min={0}
                            className={inputClass}
                            value={numericDrafts.extra_runs_limit}
                            onChange={(e) =>
                              setNumericDrafts((drafts) => ({
                                ...drafts,
                                extra_runs_limit: e.target.value,
                              }))
                            }
                            onBlur={() => commitNumericField('extra_runs_limit')}
                          />
                          <FieldHelp>{t('settings.helpExtraRuns')}</FieldHelp>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-[13px] font-medium text-slate-700">
                            {t('settings.extraPeriod')}
                          </Label>
                          <Select
                            value={form.extra_run_period}
                            onValueChange={(v) => setForm((f) => ({ ...f, extra_run_period: v }))}
                          >
                            <SelectTrigger
                              className={`w-full ${inputClass} px-3 capitalize focus:ring-[#1d4ed8]/15`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {EXTRA_PERIODS.map((p) => (
                                <SelectItem key={p} value={p} className="capitalize">
                                  {t(`settings.period.${p}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FieldHelp>{t('settings.helpExtraPeriod')}</FieldHelp>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-[13px] font-medium text-slate-700">
                            {t('settings.maxUrlsPerRun')}
                          </Label>
                          <Input
                            type="number"
                            min={1}
                            className={inputClass}
                            value={numericDrafts.max_urls_per_moderator_run}
                            onChange={(e) =>
                              setNumericDrafts((drafts) => ({
                                ...drafts,
                                max_urls_per_moderator_run: e.target.value,
                              }))
                            }
                            onBlur={() => commitNumericField('max_urls_per_moderator_run')}
                          />
                          <FieldHelp>{t('settings.helpMaxUrls')}</FieldHelp>
                        </div>
                      </div>
                    </div>

                    {/* Data limits */}
                    <div className={`${glassCard} px-4 py-4 sm:px-5 sm:py-5`}>
                      <div className="mb-4 flex items-start gap-3">
                        <SectionIcon>
                          <Database className="h-4 w-4" strokeWidth={2.25} />
                        </SectionIcon>
                        <div className="min-w-0 pt-0.5">
                          <h3 className="text-[15px] font-bold text-[#0f172a]">
                            {t('settings.sectionDataLimits')}
                          </h3>
                          <p className="mt-0.5 text-xs leading-snug text-slate-500">
                            {t('settings.sectionDataLimitsDesc')}
                          </p>
                        </div>
                      </div>

                      <div className="max-w-sm space-y-1.5">
                        <Label className="text-[13px] font-medium text-slate-700">
                          {t('settings.maxArticlesPerUrl')}
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          max={200}
                          aria-invalid={!!articlesError}
                          className={`${inputClass} ${
                            articlesError ? 'border-red-400 focus-visible:ring-red-100' : ''
                          }`}
                          value={numericDrafts.max_articles_per_url}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setNumericDrafts((drafts) => ({
                              ...drafts,
                              max_articles_per_url: raw,
                            }));
                            if (raw.trim() === '') {
                              setArticlesError(null);
                              return;
                            }
                            validateArticles(Number(raw));
                          }}
                          onBlur={() => commitNumericField('max_articles_per_url')}
                        />
                        {articlesError ? (
                          <p className="text-sm text-red-600">{articlesError}</p>
                        ) : (
                          <FieldHelp>{t('settings.helpMaxArticles')}</FieldHelp>
                        )}
                      </div>
                    </div>

                    {/* Status & notes */}
                    <div className={`${glassCard} px-4 py-4 sm:px-5 sm:py-5`}>
                      <div className="mb-4 flex items-start gap-3">
                        <SectionIcon>
                          <SlidersHorizontal className="h-4 w-4" strokeWidth={2.25} />
                        </SectionIcon>
                        <div className="min-w-0 pt-0.5">
                          <h3 className="text-[15px] font-bold text-[#0f172a]">
                            {t('settings.sectionStatusNotes')}
                          </h3>
                          <p className="mt-0.5 text-xs leading-snug text-slate-500">
                            {t('settings.sectionStatusNotesDesc')}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
                        <div className="flex items-start rounded-xl border border-sky-100/80 bg-gradient-to-br from-sky-50/90 to-blue-50/40 px-4 py-3.5">
                          <div className="space-y-1.5">
                            <ToggleSwitch
                              checked={form.scraper_enabled}
                              onChange={(v) => setForm((f) => ({ ...f, scraper_enabled: v }))}
                              label={t('settings.scraperEnabled')}
                            />
                            <p className="pl-[3.25rem] text-[11px] leading-snug text-slate-500">
                              {t('settings.scraperEnabledDesc')}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-[13px] font-medium text-slate-700">
                            {t('settings.changeReason')}{' '}
                            <span className="font-normal text-slate-400">
                              {t('settings.optional')}
                            </span>
                          </Label>
                          <textarea
                            rows={3}
                            className="w-full resize-y rounded-xl border border-slate-200/90 bg-white px-3 py-2.5 text-sm leading-relaxed text-[#0f172a] shadow-[0_1px_2px_rgba(15,23,42,0.04)] outline-none transition-[box-shadow,border-color] placeholder:text-slate-400 focus-visible:border-[#1d4ed8]/40 focus-visible:ring-[3px] focus-visible:ring-[#1d4ed8]/15"
                            value={form.reason}
                            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                            placeholder={t('settings.changeReasonPlaceholder')}
                          />
                          <FieldHelp>{t('settings.helpChangeReason')}</FieldHelp>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={saving || configLoading}
                        className="h-10 rounded-xl border-slate-200/90 bg-white/80 px-5 text-slate-700 shadow-sm hover:bg-white"
                      >
                        {t('settings.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={saving || clientId == null || !!articlesError}
                        className="h-10 gap-2 rounded-xl border-0 bg-gradient-to-r from-[#0284c7] to-[#1d4ed8] px-5 font-semibold text-white shadow-[0_6px_16px_rgba(29,78,216,0.28)] hover:brightness-105"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        {t('settings.saveConfig')}
                      </Button>
                    </div>
                  </form>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
